import {
  // TActions,
  TError,
  TFieldConfig,
  TFormField,
  TSubmitType,
} from "../../types/common";
import dayjs from "dayjs";
import {
  beautify,
  removeUnnecessaryFields,
  returnArray,
  returnObject,
} from "../../utils/helpers";
import SelectInput from "./SelectInput";
import { DateInput } from "./DateInput";
import { Wrapper } from "../styled/Styled";
import ErrorSlate from "./ErrorSlate";
import { PasswordWrapper } from "./Elements";
import { InputIconWrapper } from "./Elements";
import { Spin } from "antd";
import {
  CButton,
  CFormInput,
  CFormTextarea,
  CInputGroup,
  CInputGroupText,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import Assets from "./Assets";
import { CommonModal } from "./Modals";
import ModalForm from "./ModalForm";
import React, { useEffect, useState } from "react";
import { ChildWrapper } from "./FormSection";
import DetailsCard from "./DetailsCards";
import MapFormComponent from "./map/MapFormComponent";
import { Button, Checkbox, ConfigProvider } from "antd";
import { MODAL } from "../../utils/constants";
import FileInput from "./FileInput";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import styled from "styled-components";
import { FileDownloadButton } from "./Buttons";

interface ModalFieldWrapperProps {
  item: any;
  form: any;
  showModal: any;
  setShowModal: (modalState: any) => void;
  addItem: (fieldName: string, multiple?: boolean) => void;
  onEditItem: (item: any, index?: any) => void;
  onRemoveItem: (key: any, index: any) => void;
  onRemoveObjectItem: (key: any) => void;
  detailsView: (item: any) => any;
  error: TError;
  options: any;
  updateFormValue: (fieldName: string, value: any) => void;
  updateFormValues: (values: any) => void;
  setError: (error: TError) => void;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ModalFieldWrapper: React.FC<ModalFieldWrapperProps> = ({
  item,
  form,
  showModal,
  setShowModal,
  addItem,
  onEditItem,
  onRemoveItem,
  onRemoveObjectItem,
  detailsView,
  error,
  options,
  updateFormValue,
  updateFormValues,
  setError,
  label,
  onChange,
}) => {
  return (
    <Wrapper>
      {!(item?.props?.multiple === false && form[item?.field_name]) && (
        <ConfigProvider
          theme={{
            token: { colorPrimary: "green" },
          }}
        >
          <Button
            type="primary"
            className="font13"
            icon={item?.props?.icon}
            onClick={() => setShowModal({ show: true })}
            disabled={item?.props?.disabled}
          >
            {label}
          </Button>
        </ConfigProvider>
      )}
      <CommonModal
        title={label}
        show={showModal.show}
        onHide={() => setShowModal({ show: false })}
        onOkayPress={() => addItem(item?.field_name, item?.props?.multiple)}
        okayType="submit"
        body={
          <ChildWrapper>
            <ModalForm
              fields={item?.fields}
              field={item?.field_name}
              error={error}
              form={form}
              onChange={onChange}
              options={options}
              updateFormValue={updateFormValue}
              updateFormValues={updateFormValues}
              multiple={item?.props?.multiple}
              data={showModal.data}
              setError={setError}
            />
          </ChildWrapper>
        }
        error={error}
        formId="generic-modal-form"
      />
      <div style={{ marginTop: "0.5rem" }}>
        {item?.props?.multiple === false && form?.[item?.field_name] ? (
          <DetailsCard
            viewData={detailsView(form?.[item?.field_name])}
            onEdit={() => onEditItem(item)}
            onDelete={() => onRemoveObjectItem(item?.field_name)}
          />
        ) : (
          (form?.[item?.field_name] || []).map((it: any, idx: any) => (
            <DetailsCard
              key={idx}
              viewData={detailsView(it)}
              onEdit={() => onEditItem(item, idx)}
              onDelete={() => onRemoveItem(item?.field_name, idx)}
            />
          ))
        )}
      </div>
    </Wrapper>
  );
};

interface NumberInputProps {
  field_name: string;
  normalValue: any;
  props: any;
  label: string;
  error: any;
  updateFormValue: (fieldName: string, value: any) => void;
  getPlaceholder: () => string;
  RequiredIndicator: React.ComponentType<{ required?: boolean }>;
}

const NumberInput: React.FC<NumberInputProps> = ({
  field_name,
  normalValue,
  props,
  label,
  error,
  updateFormValue,
  getPlaceholder,
  RequiredIndicator,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = React.useState(
    normalValue !== null && normalValue !== undefined
      ? props?.showCommas === false
        ? String(normalValue)
        : Number(normalValue).toLocaleString("en-US")
      : "",
  );
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );

  // Extract minValue and maxValue from props
  const minValue = props?.minValue;
  const maxValue = props?.maxValue;

  useEffect(() => {
    if (normalValue !== null && normalValue !== undefined) {
      setDisplayValue(
        props?.showCommas === false
          ? String(normalValue)
          : Number(normalValue).toLocaleString("en-US"),
      );
      // Clear validation error when normalValue changes from outside
      setValidationError(null);
    } else {
      setDisplayValue("");
      setValidationError(null);
    }
  }, [normalValue, props?.showCommas]);

  // Validation function
  const validateValue = (value: number | null) => {
    if (value === null || isNaN(value)) {
      setValidationError(null);
      return;
    }

    if (minValue && value < minValue) {
      setValidationError(`Minimum value is ${minValue}`);
      return;
    }

    if (maxValue && value > maxValue) {
      setValidationError(`Maximum value is ${maxValue}`);
      return;
    }

    setValidationError(null);
  };

  // Additional useEffect to validate current displayValue against min/max values
  useEffect(() => {
    if ((minValue || maxValue) && displayValue) {
      const numericValue = displayValue.replace(/,/g, "");
      const currentValue = numericValue === "" ? null : Number(numericValue);
      validateValue(currentValue);
    }
  }, [displayValue, minValue, maxValue]);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    const prevValue = displayValue;

    const processed =
      props?.showCommas === false
        ? input.replace(/[^0-9]/g, "")
        : input.replace(/[^0-9,]/g, "");

    const numericValue = processed.replace(/,/g, "");

    if (numericValue === "" || !isNaN(Number(numericValue))) {
      const finalNumericValue =
        numericValue === "" ? null : Number(numericValue);

      // Validate the new value
      validateValue(finalNumericValue);

      const formattedValue =
        numericValue === ""
          ? ""
          : props?.showCommas === false
            ? numericValue
            : Number(numericValue).toLocaleString("en-US");

      setDisplayValue(formattedValue);
      updateFormValue(field_name, finalNumericValue);

      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = Math.min(
            cursorPos + (formattedValue.length - prevValue.length),
            formattedValue.length,
          );
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else {
      // Also validate current value for invalid input
      const currentValue = displayValue.replace(/,/g, "");
      const currentNumericValue =
        currentValue === "" ? null : Number(currentValue);
      validateValue(currentNumericValue);
    }
  };

  return (
    <Wrapper>
      {label ? (
        <p className="capitalize font12 mb-1">
          {label} <RequiredIndicator required={props?.required} />
        </p>
      ) : null}
      <CFormInput
        ref={inputRef}
        style={{ fontSize: 14 }}
        onChange={handleNumberChange}
        name={field_name}
        placeholder={getPlaceholder()}
        value={displayValue}
        type="text"
        min={minValue}
        max={maxValue}
        {...props}
      />
      {validationError && <ErrorSlate message={validationError} />}
      {error && error[field_name] && <ErrorSlate message={error[field_name]} />}
    </Wrapper>
  );
};

const FormFields = ({
  fields,
  sectionFields,
  error,
  form,
  onChange,
  updateFormValue,
  updateFormValues,
  options,
  labelVariables,
  submitType,
  disabledDates,
  setError,
}: TFormField): any => {
  const [showModal, setShowModal] = useState<{
    show: boolean;
    data?: any;
    action?: TSubmitType;
  }>({ show: false });

  const [notFoundOption, setNotFoundOption] = useState(false);

  const addItem = (field_name: string, multiple?: boolean) => {
    const item = form?.data;
    if (!item) return;
    delete form?.data;

    let field_data = form?.[field_name];

    if (showModal.action === "edit") {
      if (multiple === false) {
        field_data = { ...field_data, ...item };
      } else {
        field_data = field_data.map((it: any, idx: number) =>
          idx === item.index ? item : it,
        );
      }
    } else {
      if (multiple === false) {
        field_data = { ...field_data, ...item };
      } else {
        field_data = field_data ? [...field_data, item] : [item];
      }
    }

    updateFormValue(field_name, removeUnnecessaryFields(field_data));
    setShowModal({ show: false });
  };

  const removeFilePath = (form_data: any) => {
    const filePathRegex =
      /^(?:[a-zA-Z]:\\|\/)?(?:[a-zA-Z0-9_-]+[\\/])*[a-zA-Z0-9._-]+$/;
    Object.keys(form_data).forEach((key) => {
      if (
        typeof form_data[key] === "string" &&
        !filePathRegex.test(form_data[key])
      ) {
        form_data[key] = "";
      }
    });
    return form_data;
  };

  const onEditItem = (item: any, index: any = null) => {
    let form_data: any;
    if (item?.props?.multiple === false) {
      form_data = form?.[item.field_name];
    } else {
      form_data = form?.[item.field_name][index];
      form_data.index = index;
    }
    form_data = removeFilePath(form_data);

    setShowModal({
      show: true,
      data: removeUnnecessaryFields(form_data),
      action: "edit",
    });
  };
  const detailsView = (item: any) => {
    let new_item: any = {};
    Object.keys(item).map((key) => {
      if (!key.includes("-name"))
        new_item[key] =
          item[`${key}-name`] != null ? item[`${key}-name`] : item[key];
    });
    new_item = removeUnnecessaryFields(new_item);

    return new_item;
  };

  const onRemoveItem = (key: any, index: any) => {
    const array = (form[key] || []).filter((_: any, idx: any) => idx !== index);
    updateFormValue(key, removeUnnecessaryFields(array));
  };

  const onRemoveObjectItem = (key: any) => {
    updateFormValues({
      [key]: null,
    });
  };

  if (!Array.isArray(fields) && !Array.isArray(sectionFields)) return null;

  const actualFields = (fields: TFieldConfig[]) => {
    return fields.map((item, idx) => {
      const {
        type,
        placeholder,
        field_name,
        value,
        label = beautify(field_name),
        labelType,
        props,
        options_name,
        multiple,
        visible = true,
        component,
        search,
      } = item;
      const options_data = search ? search.options : item.options_data;
      const loading = search ? search.loading : item.loading ?? false;

      if (!visible) return;

      function getPlaceholder() {
        return placeholder || `Enter ${label}`;
      }
      const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props?.parent_field) {
          updateFormValue(props?.parent_field, {
            ...form?.[props?.parent_field],
            [field_name]:
              event.target.value === undefined ? null : event.target.value,
          });
          return;
        }
        if (onChange) onChange(event);
      };

      // Example modification for a text input field
      const normalValue = props?.parent_field
        ? form?.[props?.parent_field]?.[field_name]
        : form?.[field_name] || value;

      if (type === "select") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
              </p>
            ) : null}
            <SelectInputWrapper>
              <div style={{ flex: "1" }}>
                <SelectInput
                  options={(options_name && options
                    ? returnArray(options[options_name])
                    : options_data
                      ? options_data
                      : []
                  ).map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={(value) =>
                    updateFormValue
                      ? updateFormValue(field_name as string, value)
                      : null
                  }
                  value={
                    form[field_name]
                      ? isNaN(form[field_name as keyof typeof field_name])
                        ? form[field_name as keyof typeof field_name]
                        : form[field_name as keyof typeof field_name]
                      : value
                  }
                  label={label}
                  name={field_name}
                  error={error}
                  multiple={props?.multiple}
                  loading={loading}
                  acceptNewItems={props?.acceptNewItems}
                  setNotFoundOption={setNotFoundOption}
                  {...props}
                  {...(search && { onSearch: search.onSearch })}
                />
              </div>

              {item?.action_modal &&
                ((notFoundOption && props?.newLine) ||
                  form?.[item?.action_modal?.field_name]?.length > 0) && (
                  <ModalFieldWrapper
                    item={item?.action_modal}
                    form={form}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    addItem={addItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                    onRemoveObjectItem={onRemoveObjectItem}
                    detailsView={detailsView}
                    error={error}
                    options={options}
                    updateFormValue={updateFormValue}
                    updateFormValues={updateFormValues}
                    setError={setError || (() => {})}
                    label={item?.action_modal?.label}
                    onChange={onChange || (() => {})}
                  />
                )}
            </SelectInputWrapper>

            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
          </Wrapper>
        );
      }

      if (type === MODAL) {
        return (
          <ModalFieldWrapper
            item={item}
            form={form}
            showModal={showModal}
            setShowModal={setShowModal}
            addItem={addItem}
            onEditItem={onEditItem}
            onRemoveItem={onRemoveItem}
            onRemoveObjectItem={onRemoveObjectItem}
            detailsView={detailsView}
            error={error}
            options={options}
            updateFormValue={updateFormValue}
            updateFormValues={updateFormValues}
            setError={setError || (() => {})}
            label={label}
            onChange={onChange || (() => {})}
          />
        );
      }

      if (type === "tel") {
        let phone = form[field_name] || "";
        const handlePhoneOnChange = (value) => {
          phone = value;
          if (value.length < 12) {
            setError &&
              setError({
                ...(error || {}),
                [field_name]: "Phone number must be at least 12 digits.",
                notSubmit: "true",
              });
          } else {
            const updatedError = { ...error };
            delete updatedError[field_name];
            setError && setError(updatedError);
          }
          updateFormValue ? updateFormValue(field_name as string, value) : null;
        };

        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
              </p>
            ) : null}
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
            <div id={field_name}>
              <PhoneInput
                country={"ug"}
                specialLabel={""}
                value={phone}
                onChange={handlePhoneOnChange}
                placeholder="Enter phone number"
                enableSearch={true}
                containerStyle={{ width: "100%" }}
                inputClass="phone-input-custom"
                dropdownStyle={{
                  borderRadius: "8px",
                  borderColor: "#ccc",
                  boxShadow:
                    "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                }}
              />
            </div>
          </Wrapper>
        );
      }

      if (type === "date") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {labelType === "variable" ? labelVariables?.[label] : label}{" "}
                <RequiredIndicator required={props?.required} />
              </p>
            ) : null}
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
            <DateInput
              size="small"
              fullWidth
              type={type}
              id={field_name}
              name={field_name}
              placeholder={getPlaceholder()}
              value={
                form[field_name as keyof typeof field_name]
                  ? dayjs(
                      form[field_name as keyof typeof field_name],
                      props?.format || "YYYY-MM-DD",
                    )
                  : null
              }
              onChange={(value) => {
                if (updateFormValue)
                  updateFormValue(
                    field_name,
                    value?.format(props?.format || "YYYY-MM-DD"),
                  );
              }}
              format={props?.format || "YYYY-MM-DD"}
              {...props}
              disabledDate={disabledDates ? disabledDates[field_name] : null}
            />
          </Wrapper>
        );
      }

      if (type === "radio") {
        return (
          <Wrapper key={idx}>
            {label ? (
              <p
                className="capitalize font12 mb-1"
                style={{ display: "inline-block", width: "80%" }}
              >
                {label} <RequiredIndicator required={props?.required} />
              </p>
            ) : null}
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
            <div
              className="form-check"
              style={{ display: "inline-block", width: "20%" }}
            >
              {(options_name && options
                ? returnArray(options[options_name])
                : []
              ).map((item, idx) => {
                return (
                  <div key={idx} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={field_name}
                      id={item.id}
                      value={item.id}
                      checked={form[field_name] === item.id}
                      onChange={(event) => {
                        if (updateFormValue) {
                          updateFormValue(field_name, event.target.value);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={item.id}>
                      {item.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </Wrapper>
        );
      }
      if (type === "checkbox") {
        return (
          <Wrapper
            key={idx}
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: "0.8rem",
              alignItems: "center",
            }}
          >
            <Checkbox
              type="checkbox"
              id={field_name}
              name={field_name}
              value={form[field_name]}
              checked={form[field_name]}
              onChange={(event) => {
                if (updateFormValue) {
                  const checked = event.target.checked;
                  updateFormValue(field_name, checked);
                }
              }}
              required={props?.required}
            />
            {label ? (
              <label className="capitalize font12 m-0" htmlFor={field_name}>
                {label} <RequiredIndicator required={props?.required} />
              </label>
            ) : null}
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
          </Wrapper>
        );
      }
      if (type === "location") {
        return (
          <Wrapper key={idx} style={{ gridColumn: "span 2" }}>
            <MapFormComponent
              form={form}
              updateFormValues={updateFormValues}
              updateFormValue={updateFormValue}
              required={props?.required}
              error={error}
            />
          </Wrapper>
        );
      }
      if (type === "file") {
        return (
          <Wrapper key={idx}>
            {props.isDownload && (
              <FileDownloadButton
                fileUrl={props.downloadTemplateProps.filePath}
                fileName={props.downloadTemplateProps.fileName}
                text={props.downloadTemplateProps.fileText}
                title={props.downloadTemplateProps.fileTitle}
              />
            )}

            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
              </p>
            ) : null}

            <FileInput
              size="sm"
              updateFormValue={updateFormValue}
              form={form}
              is_array={props?.multiple}
              field_name={field_name}
              placeholder={getPlaceholder()}
              nested_attachment={props?.nested_attachment}
              document_type={props?.document_type}
              // value={form?.[field_name] || value}
              {...props}
            />
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
          </Wrapper>
        );
      }
      if (type === "decimal") {
        const inputRef = React.useRef<HTMLInputElement>(null);
        const [displayValue, setDisplayValue] = React.useState(
          normalValue !== null && normalValue !== undefined
            ? Number(normalValue).toLocaleString("en-US")
            : "",
        );

        useEffect(() => {
          if (normalValue !== null && normalValue !== undefined) {
            setDisplayValue(Number(normalValue).toLocaleString("en-US"));
          }
        }, [normalValue]);

        const handleDecimalChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const input = e.target.value;
          const cursorPos = e.target.selectionStart;

          // Preserve previous state for cursor calculation
          const prevValue = displayValue;

          // Process new input
          const processed = input
            .replace(/[^0-9.,]/g, "")
            .replace(/,/g, "") //
            .replace(/\.+/, ".");

          // Split into integer and decimal parts
          const [integer, decimal] = processed.split(".");
          const formattedInteger = integer.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ",",
          );
          const newValue =
            decimal !== undefined
              ? `${formattedInteger}.${decimal}`
              : formattedInteger;

          // Calculate new cursor position
          let newCursorPos = cursorPos ?? 0;
          const addedChars = newValue.length - prevValue.length;

          // Adjust position based on comma changes
          if (addedChars > 0) {
            newCursorPos += addedChars;
          } else if (addedChars < 0) {
            const removedSegment = prevValue.slice(
              newCursorPos + addedChars,
              newCursorPos,
            );
            newCursorPos -= removedSegment.split(",").length - 1;
          }

          setDisplayValue(newValue);
          updateFormValue(
            field_name,
            processed === "" ? null : Number(processed),
          );

          // Set cursor position after state update
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
          }, 0);
        };

        return (
          <Wrapper key={idx}>
            {label ? (
              <p className="capitalize font12 mb-1">
                {label} <RequiredIndicator required={props?.required} />
              </p>
            ) : null}
            <CFormInput
              ref={inputRef}
              style={{ fontSize: 14 }}
              onChange={handleDecimalChange}
              name={field_name}
              placeholder={getPlaceholder()}
              value={displayValue}
              type="text"
              {...props}
            />
            {error && error[field_name] && (
              <ErrorSlate message={error[field_name]} />
            )}
          </Wrapper>
        );
      }

      if (type === "component") {
        return <div {...props}>{component}</div>;
      }

      if (type === "number") {
        return (
          <NumberInput
            field_name={field_name}
            normalValue={normalValue}
            props={props}
            label={label}
            error={error}
            updateFormValue={updateFormValue}
            getPlaceholder={getPlaceholder}
            RequiredIndicator={RequiredIndicator}
          />
        );
      }

      return (
        <Wrapper key={idx}>
          {label ? (
            <p className="capitalize font12 mb-1">
              {label} <RequiredIndicator required={props?.required} />
            </p>
          ) : null}

          {multiple && submitType === "create" ? (
            (form?.[field_name] || [""]).map((value: any, idx: number) => {
              const handleActions = (action: "delete" | "add", idx: number) => {
                if (action === "delete") {
                  if (updateFormValue)
                    updateFormValue(
                      field_name,
                      form[field_name].filter(
                        (_item: any, id: number) => id !== idx,
                      ),
                    );
                }

                if (action === "add") {
                  if (updateFormValue)
                    updateFormValue(field_name, [
                      ...(form?.[field_name] || [""]),
                      "",
                    ]);
                }
              };
              return (
                <CInputGroup key={value}>
                  <CInputGroupText>
                    <CIcon icon={Assets[type || "text"]} />
                  </CInputGroupText>
                  {type === "textarea" ? (
                    <CFormTextarea
                      onChange={(event) => {
                        if (updateFormValue)
                          updateFormValue(field_name, [
                            ...(form?.[field_name] || [""]).map(
                              (fld: any, id: number) => {
                                if (id === idx) return event.target.value;
                                return fld;
                              },
                            ),
                          ]);
                      }}
                      placeholder={getPlaceholder()}
                      value={value}
                      {...props}
                    />
                  ) : (
                    <CFormInput
                      size="sm"
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        if (rawValue === "" || !isNaN(Number(rawValue))) {
                          const numericValue =
                            rawValue === "" ? null : Number(rawValue);
                          // Directly update form with numeric value
                          if (updateFormValue) {
                            updateFormValue(field_name, numericValue);
                          }
                        }
                      }}
                      name={field_name}
                      placeholder={getPlaceholder()}
                      value={
                        type === "number" &&
                        normalValue !== null &&
                        normalValue !== undefined
                          ? Number(normalValue).toLocaleString()
                          : normalValue
                      }
                      type={type === "number" ? "text" : type || "text"}
                      {...props}
                    />
                  )}

                  {(form?.[field_name] || [""]).length > 1 ? (
                    <CButton onClick={() => handleActions("delete", idx)}>
                      <CIcon icon={Assets["delete"]} />
                    </CButton>
                  ) : null}
                  {idx + 1 === (form?.[field_name] || [""]).length ? (
                    <CButton onClick={() => handleActions("add", idx)}>
                      <CIcon icon={Assets["plus"]} />
                    </CButton>
                  ) : null}
                </CInputGroup>
              );
            })
          ) : (
            <div>
              {type === "textarea" ? (
                <CFormTextarea
                  style={{ fontSize: 14 }}
                  onChange={onInputChange}
                  name={field_name}
                  placeholder={getPlaceholder()}
                  value={form?.[field_name]}
                  type={type || "text"}
                  {...props}
                />
              ) : type === "number" ? (
                <CFormInput
                  size="sm"
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, "");
                    if (rawValue === "" || !isNaN(Number(rawValue))) {
                      const numericValue =
                        rawValue === "" ? null : Number(rawValue);
                      // Directly update form with numeric value
                      if (updateFormValue) {
                        updateFormValue(field_name, numericValue);
                      }
                    }
                  }}
                  name={field_name}
                  placeholder={getPlaceholder()}
                  value={
                    type === "number" &&
                    normalValue !== null &&
                    normalValue !== undefined
                      ? Number(normalValue).toLocaleString()
                      : normalValue
                  }
                  type="text"
                  {...props}
                />
              ) : type === "password" ? (
                <PasswordWrapper>
                  <CFormInput
                    style={{ fontSize: 14 }}
                    onChange={onInputChange}
                    name={field_name}
                    placeholder={getPlaceholder()}
                    value={form?.[field_name]}
                    type="password"
                    {...props}
                  />
                </PasswordWrapper>
              ) : (
                <InputIconWrapper
                  icon={<Spin size="small" />}
                  showIcon={props?.loading || false}
                >
                  <CFormInput
                    style={{ fontSize: 14 }}
                    onChange={onInputChange}
                    name={field_name}
                    placeholder={getPlaceholder()}
                    value={form?.[field_name]}
                    type={type || "text"}
                    {...props}
                  />
                </InputIconWrapper>
              )}
            </div>
          )}
          {error && error[field_name] && (
            <ErrorSlate message={error[field_name]} />
          )}
        </Wrapper>
      );
    });
  };

  const renderFields = (fields: TFieldConfig[]) => {
    return (
      <div className="row" style={{ display: "flex", flexWrap: "wrap" }}>
        {fields.map((field, index) => {
          const isWide = field.props?.wide;
          const isNewLine = field.props?.newLine;
          const isSmall = field.props?.smallWidth;
          const isVisible = field.visible;
          if (isVisible == false) return;
          return (
            <React.Fragment key={index}>
              {isNewLine && <div style={{ flexBasis: "100%", height: 0 }} />}
              <div
                className={
                  isWide
                    ? "col-sm-12"
                    : isSmall
                      ? "col-sm-4"
                      : "col-lg-6 col-md-6 col-sm-12"
                }
                style={{
                  marginBottom: "1rem",
                  flexGrow: isWide ? 1 : 0,
                  ...returnObject(isWide, {
                    flexBasis: "100%",
                    maxWidth: "100%",
                  }),
                }}
              >
                {actualFields([field])}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (sectionFields) {
    return sectionFields.map((item, idx) => {
      return (
        <div key={idx}>
          <h4 className="pt-3 lead-900">{item.title}</h4>
          {renderFields(item.fields || [])}
        </div>
      );
    });
  } else if (fields) {
    return renderFields(fields);
  }
};

export const RequiredIndicator = ({ required }: { required?: boolean }) =>
  required ? <span className="text-danger">*</span> : null;

export default FormFields;

const SelectInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  flex-flow: wrap-reverse;
`;

export const FieldLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 5px;
  text-transform: capitalize;
  letter-spacing: 0.01em;
`;
